import React, { useRef } from 'react';
import html2canvas from 'html2canvas';
import './CardCanvas.css';

const CardCanvas = ({ selectedImage, cardText, setCardText }) => {
  const canvasRef = useRef(null);
  const textRef = useRef(null);

  // 画布配置常量
  const CANVAS_CONFIG = {
    // 画布基础配置
    ASPECT_RATIO: 9 / 16,     // 9:16 宽高比
    DESKTOP_WIDTH: 350,       // 桌面端画布宽度
    MOBILE_WIDTH: 350,        // 移动端画布宽度

    // 横向书写文字区域边距配置
    HORIZONTAL_TEXT_AREA: {
      TOP_MARGIN: 0.20,       // 上边距
      BOTTOM_MARGIN: 0.05,    // 下边距
      TEXT_AREA_WIDTH_RATIO: 0.8,  // 文字区域宽度占画布比例
    },

    // 纵向书写文字区域边距配置
    VERTICAL_TEXT_AREA: {
      TOP_MARGIN: 0.18,       // 上边距
      BOTTOM_MARGIN: 0.18,    // 下边距
      TEXT_AREA_WIDTH_RATIO: 0.7,  // 文字区域宽度占画布比例
    },

    // 字体大小范围
    FONT_SIZE_RANGE: {
      MIN: 12,                // 最小字体大小
      MAX: 20                 // 最大字体大小
    },

    // 文字布局配置（字符控制）
    TEXT_LAYOUT: {
      TARGET_CHARS_PER_LINE: 14,      // 目标每行字符数（横向模式）
      TARGET_CHARS_PER_COLUMN: 20,    // 目标每列字符数（纵向模式）
      MAX_CONTAINER_WIDTH_RATIO: 0.9  // 文字容器最大宽度占画布比例（安全限制）
    }

  };

  // 计算画布尺寸
  const getCanvasDimensions = () => {
    const width = window.innerWidth <= 768 ? CANVAS_CONFIG.MOBILE_WIDTH : CANVAS_CONFIG.DESKTOP_WIDTH;
    const height = Math.round(width / CANVAS_CONFIG.ASPECT_RATIO);
    return { width, height };
  };

  // 计算文字区域尺寸和位置 - 支持不同书写模式的边距配置
  const getTextAreaConfig = (canvasWidth, canvasHeight, writingMode) => {
    // 根据书写模式选择对应的边距配置
    const textAreaConfig = writingMode === 'horizontal'
      ? CANVAS_CONFIG.HORIZONTAL_TEXT_AREA
      : CANVAS_CONFIG.VERTICAL_TEXT_AREA;

    // 计算文字区域宽度（使用对应书写模式的配置，确保偶数宽度以便居中）
    const textAreaWidthRatio = textAreaConfig.TEXT_AREA_WIDTH_RATIO;
    let textAreaWidth = Math.floor(canvasWidth * textAreaWidthRatio);

    // 确保文字区域宽度为偶数，这样左右边距更容易对称
    if (textAreaWidth % 2 !== 0) {
      textAreaWidth -= 1;
    }

    // 计算高度（使用对应书写模式的上下边距配置）
    const textAreaHeight = canvasHeight * (1 - textAreaConfig.TOP_MARGIN - textAreaConfig.BOTTOM_MARGIN);

    // 精确水平居中：使用整数计算确保左右边距完全相等
    const totalMargin = canvasWidth - textAreaWidth;
    const sideMargin = Math.floor(totalMargin / 2);
    const left = sideMargin;

    // 验证计算结果（确保整数精度）
    const rightMargin = canvasWidth - left - textAreaWidth;
    const marginDifference = Math.abs(left - rightMargin);

    // 如果有余数，调整右边距以确保完全对称
    const adjustedRightMargin = totalMargin - sideMargin;

    return {
      width: textAreaWidth,
      height: textAreaHeight,
      left: left,
      top: canvasHeight * textAreaConfig.TOP_MARGIN,
      // 返回计算信息用于调试
      textAreaWidthRatio,
      sideMargin,
      rightMargin,
      adjustedRightMargin,
      totalMargin,
      marginDifference,
      isSymmetric: marginDifference < 0.1,
      // 返回使用的配置信息
      usedConfig: textAreaConfig,
      writingMode: writingMode
    };
  };

  // 基于固定文字区域宽度计算最优字体大小
  const calculateOptimalFontSize = (textAreaWidth, textAreaHeight, writingMode, TARGET_CHARS_PER_LINE) => {
    // 横向书写模式
    if (writingMode === 'horizontal') {
      // 减去内边距（左右各4px，总共8px）
      const availableWidth = textAreaWidth - 8;
      
      // 中文字符宽度约等于字体大小
      const charWidthRatio = 1.0;
      
      // 计算理想字体大小，预留5%安全边距确保不溢出
      const idealFontSize = (availableWidth / TARGET_CHARS_PER_LINE / charWidthRatio) * 0.95;
      
      // 限制在合理范围内
      const fontSize = Math.max(
        CANVAS_CONFIG.FONT_SIZE_RANGE.MIN,
        Math.min(Math.floor(idealFontSize), CANVAS_CONFIG.FONT_SIZE_RANGE.MAX)
      );
      
      return fontSize;
    } 
    // 纵向书写模式
    else {
      // 纵向书写时，我们关注的是每列能容纳多少字符
      // 对于纵向书写，我们希望每列有多少个字符是合适的
      const TARGET_CHARS_PER_COLUMN = 20; // 可以根据需要调整
      
      // 减去内边距（上下各4px，总共8px）
      const availableHeight = textAreaHeight - 8;
      
      // 计算理想字体大小，预留5%安全边距确保不溢出
      const idealFontSize = (availableHeight / TARGET_CHARS_PER_COLUMN) * 0.95;
      
      // 限制在合理范围内
      const fontSize = Math.max(
        CANVAS_CONFIG.FONT_SIZE_RANGE.MIN,
        Math.min(Math.floor(idealFontSize), CANVAS_CONFIG.FONT_SIZE_RANGE.MAX)
      );
      
      return fontSize;
    }
  };

  // 创建文字布局（仅用于垂直文字）
  const createTextLayout = (text, writingMode, textAreaConfig, fontSize) => {
    if (!text) return [];

    if (writingMode === 'horizontal') {
      // 水平文字不需要特殊布局，直接返回原文本
      return [text];
    } else {
      // 处理纵向书写中的特殊字符
      let processedText = text;
      
      // 将破折号"——"替换为竖线"|"（或其他适合纵向排版的字符）
      processedText = processedText.replace(/——/g, '｜');
      
      // 其他可能需要特殊处理的标点符号
      // 例如：将省略号"..."替换为纵向的"⋮"
      processedText = processedText.replace(/\.\.\./g, '⋮');
      processedText = processedText.replace(/…/g, '⋮');
      
      // 垂直文字布局
      // 计算每列可容纳的最大字符数
      const maxCharsPerColumn = Math.floor(textAreaConfig.height / fontSize);
      
      // 计算需要多少列来容纳所有文字
      const totalColumns = Math.ceil(processedText.length / maxCharsPerColumn);
      
      // 计算列宽（考虑字体大小和间距）
      const columnSpacing = fontSize * 1.8; // 列间距为字体大小的1.8倍
      
      // 确保所有列都能在文本区域内显示
      const availableWidth = textAreaConfig.width;
      const requiredWidth = totalColumns * columnSpacing;
      
      // 如果需要的宽度超过可用宽度，调整每列字符数
      let adjustedMaxCharsPerColumn = maxCharsPerColumn;
      if (requiredWidth > availableWidth && totalColumns > 1) {
        const maxPossibleColumns = Math.floor(availableWidth / columnSpacing);
        adjustedMaxCharsPerColumn = Math.ceil(processedText.length / maxPossibleColumns);
      }
      
      // 创建列
      const columns = [];
      let currentIndex = 0;

      while (currentIndex < processedText.length) {
        const columnText = processedText.slice(currentIndex, currentIndex + adjustedMaxCharsPerColumn);
        columns.push(columnText);
        currentIndex += adjustedMaxCharsPerColumn;
      }

      return columns;
    }
  };

  // 移除所有拖拽相关的处理函数
  // const handleTextMouseDown = (e) => {...}
  // const handleMouseMove = useCallback((e) => {...}, []);
  // const handleMouseUp = useCallback(() => {...}, []);
  
  // 移除拖拽相关的useEffect
  // useEffect(() => {...}, []);

  const exportCard = async () => {
    if (canvasRef.current) {
      try {
        // Show loading state
        const exportBtn = document.querySelector('.export-btn');
        const originalText = exportBtn.textContent;
        exportBtn.textContent = '导出中...';
        exportBtn.disabled = true;

        // Mobile-optimized export settings
        const isMobile = window.innerWidth <= 768;
        const canvas = await html2canvas(canvasRef.current, {
          backgroundColor: selectedImage ? null : '#ffffff',
          scale: isMobile ? 4 : 3, // 移动端4x，桌面端3x
          useCORS: true,
          allowTaint: true,
          width: canvasRef.current.offsetWidth,
          height: canvasRef.current.offsetHeight,
          // 提高背景图片清晰度的关键设置
          pixelRatio: Math.max(window.devicePixelRatio || 1, 2),
          logging: false,
          foreignObjectRendering: false, // 改为false以获得更好的背景图片渲染效果
          imageTimeout: 0, // 确保图片完全加载
          removeContainer: true
        });

        const link = document.createElement('a');
        const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
        link.download = `picture-card-${timestamp}.png`;
        link.href = canvas.toDataURL('image/png', 1.0);
        link.click();

        // Reset button state
        exportBtn.textContent = originalText;
        exportBtn.disabled = false;

      } catch (error) {
        console.error('Error exporting card:', error);
        alert('导出卡片时出错。请重试。');

        // Reset button state on error
        const exportBtn = document.querySelector('.export-btn');
        exportBtn.textContent = '下载卡片';
        exportBtn.disabled = false;
      }
    }
  };



  return (
    <div className="card-canvas-container">
      <div className="canvas-header">
        <h3>卡片预览</h3>
        <button onClick={exportCard} className="export-btn">
          下载卡片
        </button>
      </div>
      
      <div
        ref={canvasRef}
        className="card-canvas"
        style={{
          backgroundColor: selectedImage ? 'transparent' : '#fef7f7',
          backgroundSize: 'cover', // Cover ensures no white edges, crops excess
          backgroundPosition: 'center center', // Center the image
          backgroundRepeat: 'no-repeat',
          // Force consistent background rendering across devices
          backgroundAttachment: 'scroll',
          // Ensure consistent rendering across devices
          imageRendering: 'auto',
          WebkitBackgroundSize: 'cover',
          MozBackgroundSize: 'cover',
          OBackgroundSize: 'cover',
          // Additional properties to force consistency
          backgroundOrigin: 'border-box', // Use full canvas area
          backgroundClip: 'border-box' // Clip to full canvas area
        }}
      >
        {/* 使用img元素而不是背景图片，以提高导出时的清晰度 */}
        {selectedImage && (
          <img 
            src={selectedImage} 
            alt="Card background" 
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              zIndex: -1
            }}
          />
        )}
        
        {!selectedImage && !selectedImage && (
          <div className="placeholder-text">
            选择一张图片吧！
          </div>
        )}

        <div
          ref={textRef}
          className={`card-text-container ${cardText.writingMode === 'horizontal' ? 'horizontal-mode' : ''}`}
          style={{
            position: 'absolute',
            userSelect: 'none',
            touchAction: 'none'
          }}
        >
          {(() => {
            // 获取画布尺寸
            const canvasHeight = canvasRef.current ? canvasRef.current.offsetHeight :
              (window.innerWidth <= 768 ? 498 : 622);
            const canvasWidth = canvasRef.current ? canvasRef.current.offsetWidth :
              (window.innerWidth <= 768 ? 280 : 350);

            // 目标字符数（可在配置中调整）
            const TARGET_CHARS_PER_LINE = CANVAS_CONFIG.TEXT_LAYOUT.TARGET_CHARS_PER_LINE;

            // 确定书写模式
            const writingMode = cardText.writingMode || 'vertical';

            // 计算文字区域配置（根据书写模式使用对应的边距配置）
            const textAreaConfig = getTextAreaConfig(canvasWidth, canvasHeight, writingMode);

            // 计算最优字体大小
            const optimalFontSize = calculateOptimalFontSize(
              textAreaConfig.width, 
              textAreaConfig.height,
              writingMode, 
              CANVAS_CONFIG.TEXT_LAYOUT.TARGET_CHARS_PER_LINE
            );

            // 创建文字布局
            const textLayout = createTextLayout(cardText.content, writingMode, textAreaConfig, optimalFontSize);

            // 调试信息
            const availableWidthForText = textAreaConfig.width - 8; // 减去内边距
            const estimatedCharsPerLine = Math.floor(availableWidthForText / (optimalFontSize * 1.0));

            // 精确计算实际的左右边距
            const containerLeftMargin = textAreaConfig.left; // 文字容器左边距
            const containerRightMargin = canvasWidth - textAreaConfig.left - textAreaConfig.width; // 文字容器右边距
            const textLeftMargin = containerLeftMargin + 4; // 文字左边距（容器边距 + 内边距）
            const textRightMargin = containerRightMargin + 4; // 文字右边距（容器边距 + 内边距）

            console.log('Canvas Layout Debug (精确整数边距):', {
              canvasWidth,
              canvasHeight,
              TARGET_CHARS_PER_LINE,
              textAreaConfig: {
                width: textAreaConfig.width,
                height: textAreaConfig.height,
                left: textAreaConfig.left,
                top: textAreaConfig.top,
                textAreaWidthRatio: textAreaConfig.textAreaWidthRatio,
                totalMargin: textAreaConfig.totalMargin,
                sideMargin: textAreaConfig.sideMargin,
                rightMargin: textAreaConfig.rightMargin,
                adjustedRightMargin: textAreaConfig.adjustedRightMargin,
                marginDifference: textAreaConfig.marginDifference,
                isSymmetric: textAreaConfig.isSymmetric
              },
              availableWidthForText,
              optimalFontSize,
              estimatedCharsPerLine,
              writingMode,
              margins: {
                topMargin: `${textAreaConfig.usedConfig.TOP_MARGIN * 100}%`,
                bottomMargin: `${textAreaConfig.usedConfig.BOTTOM_MARGIN * 100}%`,
                containerLeftMargin: `${containerLeftMargin.toFixed(0)}px`,
                containerRightMargin: `${containerRightMargin.toFixed(0)}px`,
                textLeftMargin: `${textLeftMargin.toFixed(0)}px`,
                textRightMargin: `${textRightMargin.toFixed(0)}px`,
                containerSymmetric: Math.abs(containerLeftMargin - containerRightMargin) < 0.1,
                textSymmetric: Math.abs(textLeftMargin - textRightMargin) < 0.1,
                // 书写模式边距验证
                writingModeUsed: writingMode,
                configUsed: writingMode === 'horizontal' ? 'HORIZONTAL_TEXT_AREA' : 'VERTICAL_TEXT_AREA',
                marginSpec: `上下各${textAreaConfig.usedConfig.TOP_MARGIN * 100}%边距，左右各${(1 - textAreaConfig.usedConfig.TEXT_AREA_WIDTH_RATIO) * 50}%边距`,
                // 验证计算
                verification: {
                  leftPlusWidthPlusRight: `${containerLeftMargin} + ${textAreaConfig.width} + ${containerRightMargin} = ${containerLeftMargin + textAreaConfig.width + containerRightMargin}`,
                  shouldEqualCanvas: canvasWidth
                }
              },
              padding: '4px (left/right)',
              layout: '整数精度水平居中',
              device: canvasWidth === 350 ? 'Desktop' : 'Mobile'
            });

            if (writingMode === 'horizontal') {
              // 水平文字渲染 - 通过字体大小控制每行15个字符
              return (
                <div
                  className="horizontal-text-container"
                  style={{
                    position: 'absolute',
                    left: `${textAreaConfig.left}px`,
                    top: `${textAreaConfig.top}px`,
                    width: `${textAreaConfig.width}px`,
                    height: `${textAreaConfig.height}px`,
                    fontSize: `${optimalFontSize}px`,
                    color: cardText.color,
                    fontFamily: cardText.fontFamily,
                    lineHeight: 1.4,
                    overflow: 'hidden',
                    whiteSpace: 'pre-wrap', // 保持换行符，允许自动换行
                    wordWrap: 'break-word', // 长单词自动换行
                    wordBreak: 'normal', // 允许中文正常换行，英文在单词边界换行
                    overflowWrap: 'break-word', // 超长单词换行
                    letterSpacing: '0px', // 控制字间距
                    textAlign: cardText.textAlign || 'justify', // 使用用户选择的对齐方式，默认两端对齐
                    textJustify: (cardText.textAlign === 'justify' || !cardText.textAlign) ? 'inter-character' : 'auto', // 只在两端对齐时启用字符间对齐
                    // 修复说明：使用 normal 允许中文正常换行，配合 overflowWrap 处理长单词
                    // 添加对称的内边距确保左右边距一致
                    padding: '0 4px',
                    boxSizing: 'border-box',
                    // 跨设备字体渲染一致性
                    WebkitTextSizeAdjust: 'none',
                    textSizeAdjust: 'none',
                    WebkitFontSmoothing: 'antialiased',
                    MozOsxFontSmoothing: 'grayscale',
                    // 标准化文字阴影
                    textShadow: '2px 2px 4px rgba(0, 0, 0, 0.5)',
                    fontWeight: cardText.fontWeight || 'bold',
                    fontStyle: cardText.fontStyle || 'normal'
                  }}
                >
                  {cardText.content}
                </div>
              );
            } else {
              // 垂直文字渲染
              const columnSpacing = optimalFontSize * 1.8;
              const startX = textAreaConfig.left + textAreaConfig.width - optimalFontSize;

              return textLayout.map((columnText, columnIndex) => (
                <div
                  key={columnIndex}
                  className="vertical-text-column"
                  style={{
                    position: 'absolute',
                    left: `${startX - columnIndex * columnSpacing}px`,
                    top: `${textAreaConfig.top}px`,
                    width: `${optimalFontSize}px`,
                    height: `${textAreaConfig.height}px`,
                    fontSize: `${optimalFontSize}px`,
                    color: cardText.color,
                    fontFamily: cardText.fontFamily,
                    writingMode: 'vertical-rl',
                    textOrientation: 'upright',
                    lineHeight: 1.4,
                    overflow: 'hidden',
                    // 跨设备字体渲染一致性
                    WebkitTextSizeAdjust: 'none',
                    textSizeAdjust: 'none',
                    WebkitFontSmoothing: 'antialiased',
                    MozOsxFontSmoothing: 'grayscale',
                    // 标准化文字阴影
                    textShadow: '2px 2px 4px rgba(0, 0, 0, 0.5)',
                    fontWeight: cardText.fontWeight || 'bold',
                    fontStyle: cardText.fontStyle || 'normal'
                  }}
                >
                  {columnText}
                </div>
              ));
            }
          })()}
        </div>
      </div>
    </div>
  );
};

export default CardCanvas;
